import { useRef, useState, useTransition } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';
import { debounce } from 'lodash';
import { useLocation } from 'react-router';

import { SidebarMenuSubItem, useSidebar } from '@/components/ui/sidebar';
import { InputSearch } from '@/components/ui/input-search';
import { AppsDocument } from 'src/graphql/actions/apps/apps.query';
import { withSuspense } from 'src/hoc';
import { isLinkActive } from 'src/utils/is-link-active';

import { SidebarAppRow, SidebarAppsList, SidebarAppsSkeleton } from './ui';

const PER_PAGE = 10;

const SidebarAppsContainer = () => {
  const { pathname } = useLocation();
  const { isMobile, toggleSidebar } = useSidebar();
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [isSearchPending, startSearchTransition] = useTransition();
  const debouncedCommitQuery = useRef(
    debounce((value: string) => startSearchTransition(() => setQuery(value)), 300),
  ).current;
  const { data } = useSuspenseQuery(AppsDocument, {
    variables: { page: 1, perPage: PER_PAGE, ...(query && { query }) },
  });

  const handleNavigate = isMobile ? toggleSidebar : undefined;

  const showSearch = query !== '' || data.apps.nodesCount > PER_PAGE;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    debouncedCommitQuery(event.target.value);
  };

  const handleSearchClear = () => {
    debouncedCommitQuery.cancel();
    setInputValue('');
    startSearchTransition(() => setQuery(''));
  };

  return (
    <div className="flex flex-1 flex-col">
      {showSearch && (
        <div className="shrink-0 px-0.5 pt-1">
          <InputSearch
            placeholder="Search all apps"
            value={inputValue}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            isLoading={isSearchPending}
            className="[&>input]:h-8 [&>input]:bg-transparent [&>input]:border-0"
          />
        </div>
      )}
      <SidebarAppsList>
        {data.apps.nodes.length === 0 && (
          <SidebarMenuSubItem className="px-2.5 py-1.5 text-sm text-muted-foreground">
            {query ? 'No matches' : 'No applications yet'}
          </SidebarMenuSubItem>
        )}
        {data.apps.nodes.map((app) => (
          <SidebarAppRow
            key={app.id}
            app={app}
            isActive={isLinkActive(pathname, `/applications/${app.id}`)}
            onNavigate={handleNavigate}
          />
        ))}
      </SidebarAppsList>
    </div>
  );
};

export default withSuspense(SidebarAppsContainer, SidebarAppsSkeleton);
